/* ============================================================================
   Kawaii catalog (2026 design): products for the random pickers from a
   whole collection, not just the first 50 that Liquid can see, and a
   memory of what each picker has already shown this shopper.

   kcCatalog.load(feedUrl)      every in-stock product of a collection, from
                                templates/collection.surprise-data.liquid:
                                250 a page, page 1 says how many pages there
                                are and the rest are fetched together. Kept
                                for the page, and in sessionStorage for 20
                                minutes so moving around the site is free.
   kcCatalog.feed(url)          a collection URL's feed URL.
   kcCatalog.draw(key, list, count, avoid)
                                `count` products at random from `list` that
                                this picker hasn't shown lately. The memory
                                (localStorage, by `key`) outlives the visit:
                                a product only comes back once the rest of
                                the list has had its turn. `avoid` (product
                                URLs) keeps products already on the screen
                                out while there are others to show.
   kcCatalog.seen(key) / remember(key, products)
                                the same memory for pickers that rank
                                rather than draw.
   kcCatalog.sized(url, width)  a Shopify image URL at another width.
   kcCatalog.shuffle(list)      a shuffled copy.

   Used by the home page's Surprise machine (kc-blind-box), the gift finder
   (kc-gift-finder), the header's polaroid piles and search picks
   (section-kc-header.js) and the gift finder page (section-surprise.js).
   ============================================================================ */
(() => {
  if (window.kcCatalog) return;

  const TTL = 20 * 60 * 1000;
  const CACHE = 'kawaii-surprise:v2:';
  const SEEN = 'kc-seen:';
  const SEEN_MAX = 400;
  const pools = new Map();

  const shuffle = (list) => {
    const copy = list.slice();
    for (let i = copy.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
  };

  /* --- the feed ------------------------------------------------------------ */

  const readCache = (url) => {
    try {
      const stored = JSON.parse(sessionStorage.getItem(CACHE + url));
      if (stored && Date.now() - stored.time < TTL && Array.isArray(stored.products)) return stored.products;
    } catch (e) {}
    return null;
  };

  const writeCache = (url, products) => {
    try {
      sessionStorage.setItem(CACHE + url, JSON.stringify({ time: Date.now(), products }));
    } catch (e) {
      // Too big for this session's storage: it's fetched again next page.
    }
  };

  const fetchPage = (url, page) => {
    const joiner = url.includes('?') ? '&' : '?';
    return fetch(`${url}${joiner}page=${page}`, { credentials: 'same-origin' }).then((response) => {
      if (!response.ok) throw new Error(`Catalog feed answered ${response.status}`);
      return response.json();
    });
  };

  const load = (url) => {
    if (!url) return Promise.reject(new Error('Catalog: no feed'));
    if (pools.has(url)) return pools.get(url);

    const cached = readCache(url);
    const request = cached
      ? Promise.resolve(cached)
      : fetchPage(url, 1).then((first) => {
          const rest = [];
          for (let page = 2; page <= first.pages; page++) rest.push(fetchPage(url, page));
          return Promise.all(rest).then((pages) => {
            const products = pages.reduce((all, next) => all.concat(next.products), first.products);
            writeCache(url, products);
            return products;
          });
        });
    // Let the next attempt try again rather than replaying a failure.
    request.catch(() => pools.delete(url));
    pools.set(url, request);
    return request;
  };

  const feed = (url) => (url ? `${String(url).split('?')[0]}?view=surprise-data` : '');

  const sized = (url, width) => {
    if (!url) return '';
    if (/[?&]width=\d+/.test(url)) return url.replace(/([?&])width=\d+/, `$1width=${width}`);
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  };

  /* --- what each picker has shown ------------------------------------------ */

  const idOf = (item) => String((item && item.url) || '').split('?')[0];

  const readSeen = (key) => {
    try {
      const list = JSON.parse(localStorage.getItem(SEEN + key));
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  };

  const writeSeen = (key, list) => {
    try {
      localStorage.setItem(SEEN + key, JSON.stringify(list.slice(-SEEN_MAX)));
    } catch (e) {}
  };

  const draw = (key, list, count, avoid) => {
    const n = Math.max(0, Math.min(count, list.length));
    if (!n) return [];

    const seen = readSeen(key);
    const seenAt = new Map(seen.map((id, i) => [id, i]));
    const avoidSet = new Set((avoid || []).map((url) => String(url).split('?')[0]));
    const fresh = [];
    const stale = [];
    const held = [];
    list.forEach((item) => {
      const id = idOf(item);
      if (avoidSet.has(id)) held.push(item);
      else if (seenAt.has(id)) stale.push(item);
      else fresh.push(item);
    });
    // Short of fresh ones: the longest ago first, then what's on screen.
    stale.sort((a, b) => seenAt.get(idOf(a)) - seenAt.get(idOf(b)));
    const picks = shuffle(fresh).concat(stale, shuffle(held)).slice(0, n);

    // Remember these last. Of this list, keep only as many as leaves `n`
    // unshown for next time; the longest ago go back in.
    const ids = new Set(picks.map(idOf));
    let next = seen.filter((id) => !ids.has(id)).concat(Array.from(ids));
    const inList = new Set(list.map(idOf));
    let over = next.filter((id) => inList.has(id)).length - Math.max(0, list.length - held.length - n);
    if (over > 0) {
      next = next.filter((id) => {
        if (over > 0 && inList.has(id) && !ids.has(id)) {
          over--;
          return false;
        }
        return true;
      });
    }
    writeSeen(key, next);
    return picks;
  };

  // For pickers that rank rather than draw (the gift quiz): what this
  // picker has shown, and a way to add to it.
  const seen = (key) => new Set(readSeen(key));

  const remember = (key, items) => {
    const ids = new Set(items.map(idOf).filter(Boolean));
    writeSeen(key, readSeen(key).filter((id) => !ids.has(id)).concat(Array.from(ids)));
  };

  window.kcCatalog = { load, feed, draw, seen, remember, sized, shuffle };
})();
